// Audio playback module for Gemini Live Audio

// Audio playback state
let audioContext = null;
let audioElement = null;
let audioQueue = [];
let isPlaying = false;
let processingBuffer = new Float32Array(0); // Buffer for incoming audio data
let scheduledTime = 0; // Time next buffer is scheduled to play
let isStreamComplete = false; // Flag to indicate if the audio stream is finished
let playbackRate = 1.0; // Variable to control playback speed
let storedProcessingBuffer = null; // To store processing buffer when stopping
let storedAudioQueue = null; // To store audio queue when stopping
let scheduleTimeoutId = null; // To store the ID of the pending schedulePlayback timeout
// Track active buffer sources so we can stop them when needed
let activeSources = [];

// --- new code: store a completion callback ---
let onPlaybackComplete = null;

// --- NEW: Seeking support ---
let storedChunks = []; // Store all received chunks for seeking
let totalDuration = 0; // Total duration in seconds
let currentPlaybackTime = 0; // Current playback position in seconds
let lastUpdateTime = 0; // Last time we updated current position
let seekTarget = null; // Target time for seeking
let onProgressUpdate = null; // Callback for progress updates

// Constants
const SAMPLE_RATE = 24000;
const BUFFER_SIZE = 7680; // ~320ms buffer size
const SCHEDULE_AHEAD_TIME = 0.2; // Schedule 200ms ahead
const INITIAL_BUFFER_TIME = 0.05; // Wait 50ms before starting playback

function isVerboseAudioPlaybackLoggingEnabled() {
    if (typeof window === 'undefined') {
        return false;
    }

    try {
        const fromStorage = String(localStorage.getItem('curio.audio.playbackVerboseLogs') || '').trim().toLowerCase();
        if (fromStorage === '1' || fromStorage === 'true') {
            return true;
        }
    } catch (_) {}

    try {
        const queryValue = new URLSearchParams(window.location.search).get('audioPlaybackVerboseLogs');
        if (queryValue === '1' || queryValue === 'true') {
            return true;
        }
    } catch (_) {}

    return false;
}

const AUDIO_PLAYBACK_VERBOSE_LOGS = isVerboseAudioPlaybackLoggingEnabled();

function audioPlayerLog(...args) {
    if (!AUDIO_PLAYBACK_VERBOSE_LOGS) return;
    console.info(...args);
}

/**
 * Initialize the audio context and gain node
 * @returns {boolean} True if initialization was successful
 */
export function initialize() {
    try {
        // Only create AudioContext if it doesn't exist yet
        if (!audioContext) {
            audioContext = new (window.AudioContext || window.webkitAudioContext)({
                sampleRate: SAMPLE_RATE // Match the sample rate of Gemini's audio (24kHz)
            });
            
            // Create a gain node for volume control
            audioElement = audioContext.createGain();
            audioElement.connect(audioContext.destination);
            
            // Ensure volume is at 100%
            if (audioElement && audioElement.gain) {
                audioElement.gain.value = 1.0;
            }
            
            audioPlayerLog("Audio context initialized successfully at sample rate:", audioContext.sampleRate);
        }
        
        // Resume the context (important for autoplay policy)
        if (audioContext.state !== 'running') {
            audioContext.resume().then(() => {
                audioPlayerLog("AudioContext resumed successfully during initialization");
            }).catch(err => {
                console.error("Failed to resume AudioContext during initialization:", err);
            });
        }
        
        return true;
    } catch (error) {
        console.error("Error initializing audio context:", error);
        return false;
    }
}

/**
 * Set the desired playback speed.
 * @param {number} rate - The desired playback rate (e.g., 1.0 for normal, 2.0 for double speed).
 */
export function setPlaybackRate(rate) {
    if (rate > 0) { // Basic validation: rate must be positive
        playbackRate = rate;
        audioPlayerLog(`Playback rate set to: ${playbackRate}`);
        // Note: This change applies to newly scheduled buffers.
        // Already scheduled buffers will continue at their previous rate.
    } else {
        console.warn("Invalid playback rate requested:", rate);
    }
}

/**
 * Convert raw PCM16 ArrayBuffer to Float32Array
 * @param {ArrayBuffer} pcmData - The raw PCM16 audio data (little-endian)
 * @returns {Float32Array} - The audio data as Float32Array (-1.0 to 1.0)
 */
function pcm16ToFloat32(pcmData) {
    const dataView = new DataView(pcmData);
    const numSamples = pcmData.byteLength / 2;
    const float32Array = new Float32Array(numSamples);
    
    for (let i = 0; i < numSamples; i++) {
        try {
            // Read Int16, explicitly little-endian (true)
            const int16Val = dataView.getInt16(i * 2, true);
            // Normalize to -1.0 to 1.0
            float32Array[i] = int16Val / 32768.0;
        } catch (e) {
            console.error(`Error reading Int16 at index ${i * 2}:`, e);
            // Assign 0 or handle error appropriately
            float32Array[i] = 0; 
        }
    }
    
    return float32Array;
}

/**
 * Process incoming PCM audio data, buffer it, and manage playback scheduling.
 * @param {ArrayBuffer} rawPcmData - The raw PCM16 audio data chunk (from Blob or base64)
 */
export function playPCMAudio(rawPcmData) {
    if (!audioContext || rawPcmData.byteLength === 0) {
        console.warn("Skipping empty or invalid audio data, or audio context not ready.");
        return;
    }
    
    // If we are starting playback of a new stream after the previous one completed,
    // reset the completion flag.
    if (isStreamComplete) {
        audioPlayerLog("Resetting stream completion flag for new audio data.");
        isStreamComplete = false;
    }

    // Ensure context is running (important for autoplay policies)
    if (audioContext.state !== 'running') {
        audioContext.resume().then(() => {
            audioPlayerLog("AudioContext resumed for playback.");
            processAndBufferData(rawPcmData);
        }).catch(err => {
            console.error("Failed to resume AudioContext for playback:", err);
        });
    } else {
        processAndBufferData(rawPcmData);
    }
}

/**
 * Internal function to convert, buffer, and schedule audio data.
 * @param {ArrayBuffer} rawPcmData 
 */
function processAndBufferData(rawPcmData) {
    const float32Chunk = pcm16ToFloat32(rawPcmData);

    // Append new data to the processing buffer
    const newBuffer = new Float32Array(processingBuffer.length + float32Chunk.length);
    newBuffer.set(processingBuffer, 0);
    newBuffer.set(float32Chunk, processingBuffer.length);
    processingBuffer = newBuffer;

    // Slice full buffers from processingBuffer and push to queue
    while (processingBuffer.length >= BUFFER_SIZE) {
        const bufferToQueue = processingBuffer.slice(0, BUFFER_SIZE);
        audioQueue.push(bufferToQueue);
        
        // Store chunk for seeking support
        storedChunks.push(bufferToQueue.slice()); // Create a copy
        
        // Update total duration
        const chunkDuration = BUFFER_SIZE / SAMPLE_RATE;
        totalDuration += chunkDuration;
        
        processingBuffer = processingBuffer.slice(BUFFER_SIZE);
    }

    // If not already playing, start the scheduling process
    if (!isPlaying && audioQueue.length > 0) {
        isPlaying = true;
        // Initialize scheduledTime with a small buffer
        scheduledTime = audioContext.currentTime + INITIAL_BUFFER_TIME;
        lastUpdateTime = audioContext.currentTime;
        currentPlaybackTime = 0; // Reset playback time
        audioPlayerLog("Starting playback scheduling. Initial queue size:", audioQueue.length);
        // Clear any potentially lingering timeout before starting anew
        if (scheduleTimeoutId) {
            clearTimeout(scheduleTimeoutId);
            scheduleTimeoutId = null;
        }
        schedulePlayback(); 
    } else if (isPlaying) {
         // If already playing, ensure scheduler is running if needed
         audioPlayerLog("Audio data added to queue. Queue size:", audioQueue.length, "Processing buffer size:", processingBuffer.length);
    }
}

/**
 * Schedules audio buffer playback using lookahead timing.
 */
function schedulePlayback() {
    // Clear any previous timeout ID now that we are running
    if (scheduleTimeoutId) {
      clearTimeout(scheduleTimeoutId);
      scheduleTimeoutId = null;
    }

    if (!isPlaying) { // Check if playback was stopped since the timeout was set
        audioPlayerLog("schedulePlayback called but isPlaying is false. Exiting.");
        return;
    }

    // Ensure context is running before scheduling
    if (audioContext.state !== 'running') {
        console.warn("AudioContext not running, attempting resume...");
        audioContext.resume().then(() => {
             audioPlayerLog("AudioContext resumed during scheduling.");
             // Re-attempt scheduling after a short delay to allow resume
             scheduleTimeoutId = setTimeout(schedulePlayback, 50); 
        }).catch(err => {
            console.error("Failed to resume AudioContext during scheduling:", err);
            isPlaying = false; // Stop playback if context can't be resumed
        });
        return; // Exit and retry after resume attempt
    }

    // Schedule buffers as long as there's data and we are ahead of currentTime
    while (audioQueue.length > 0 && scheduledTime < audioContext.currentTime + SCHEDULE_AHEAD_TIME) {
        const float32Buffer = audioQueue.shift();
        const audioBuffer = convertPCMToAudioBuffer(float32Buffer);

        const source = audioContext.createBufferSource();
        source.buffer = audioBuffer;
        
        // Add to active sources for potential stopping later
        activeSources.push(source);
        // Clean up when the source ends
        source.onended = () => {
            const idx = activeSources.indexOf(source);
            if (idx !== -1) activeSources.splice(idx, 1);
        };
        
        // Ensure gain node exists and connect
        if (!audioElement) {
            audioElement = audioContext.createGain();
            audioElement.gain.value = 1.0; // Full volume
            audioElement.connect(audioContext.destination);
            audioPlayerLog("Gain node created during scheduling.");
        }
        source.connect(audioElement);

        // Apply the current playback rate
        source.playbackRate.value = playbackRate;

        // Calculate precise start time, ensuring it's not in the past
        const startTime = Math.max(scheduledTime, audioContext.currentTime);
        source.start(startTime);

        // Update the time for the next buffer, accounting for playback rate
        const effectiveDuration = audioBuffer.duration / source.playbackRate.value;
        scheduledTime = startTime + effectiveDuration;
        
        audioPlayerLog(`Scheduled buffer. Rate: ${source.playbackRate.value.toFixed(2)}, Start: ${startTime.toFixed(3)}, Orig Dur: ${audioBuffer.duration.toFixed(3)}, Eff Dur: ${effectiveDuration.toFixed(3)}, Next schedule: ${scheduledTime.toFixed(3)}, Queue: ${audioQueue.length}`);
    }

    // Update progress and notify callback
    if (onProgressUpdate && isPlaying) {
        const currentTime = getCurrentTime();
        onProgressUpdate(currentTime, totalDuration);
    }

    // Check if we need to continue scheduling
    if (audioQueue.length > 0) {
        // Calculate time until the next scheduling check is needed
        const timeUntilNextCheck = (scheduledTime - audioContext.currentTime - SCHEDULE_AHEAD_TIME / 2) * 1000;
        const delay = Math.max(50, timeUntilNextCheck); // Check at least every 50ms or sooner if needed
        audioPlayerLog(`Next scheduling check in ${delay.toFixed(0)} ms`);
        scheduleTimeoutId = setTimeout(schedulePlayback, delay);
    } else {
        if (isStreamComplete) {
            // If streaming is done and the queue is empty, we might still have
            // buffers that have already been scheduled but not finished playing.
            if (activeSources.length === 0 ) {
                // No more queued data *and* no active sources – playback truly is finished.
                audioPlayerLog("Audio queue empty, stream complete and all sources finished. Stopping playback.");
                isPlaying = false; // Playback truly complete

                // --- new code: invoke the completion callback ---
                if (typeof onPlaybackComplete === 'function') {
                    try {
                        onPlaybackComplete();
                    } catch (e) {
                        console.error("Error in playback complete callback", e);
                    }
                }

            } else {
                // There are still sources playing out – check again shortly.
                audioPlayerLog(`Stream complete but waiting for ${activeSources.length} active sources to finish.`);
                scheduleTimeoutId = setTimeout(schedulePlayback, 100); // Poll until sources end
            }
        } else if (isPlaying) {
             // Queue is empty, but stream not marked complete yet, and we are "playing".
             // Schedule another check shortly in case more data arrives.
             audioPlayerLog("Audio queue empty, scheduling check in 100ms.");
             scheduleTimeoutId = setTimeout(schedulePlayback, 100); // Check again soon for more data
        } else {
            // Queue is empty and isPlaying is false (either stopped or never started).
             audioPlayerLog("Audio queue empty and playback stopped/not started.");
        }
    }
}

/**
 * Convert Float32Array audio data to an AudioBuffer
 * @param {Float32Array} float32Data - The Float32 audio data (-1.0 to 1.0)
 * @returns {AudioBuffer} - The audio buffer for playback
 */
function convertPCMToAudioBuffer(float32Data) {
    // Create an audio buffer (1 channel, length of the float data, sample rate)
    const audioBuffer = audioContext.createBuffer(1, float32Data.length, SAMPLE_RATE);
    
    // Copy the Float32 data directly into the audio buffer's channel data
    audioBuffer.getChannelData(0).set(float32Data);
    
    return audioBuffer;
}

/**
 * Convert base64 to ArrayBuffer
 * @param {string} base64 - Base64 string
 * @returns {ArrayBuffer} - The array buffer
 */
export function base64ToArrayBuffer(base64) {
    // Add logging for input length
    audioPlayerLog('base64ToArrayBuffer input length:', base64?.length ?? 'null');
    try {
        // Clean base64 string by removing whitespace and preserving padding '='
        const cleanBase64 = base64.replace(/\s/g, '').replace(/[^A-Za-z0-9+\/=]/g, '');
        
        // Standard base64 decoding
        const binaryString = atob(cleanBase64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        
        for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        
        audioPlayerLog(`Decoded base64 string to ${len} bytes`);
        return bytes.buffer;
    } catch (error) {
        // Add more context to error logging
        console.error(`Error decoding base64 data (length ${base64?.length}):`, error, 'Input preview:', base64?.substring(0, 50));
        return new ArrayBuffer(0); // Return empty buffer on error
    }
}

/**
 * Play a test tone to verify audio is working
 */
export function playTestTone() {
    if (!audioContext) {
        try {
            audioContext = new (window.AudioContext || window.webkitAudioContext)({
                sampleRate: SAMPLE_RATE
            });
        } catch (error) {
            console.error("Failed to create audio context:", error);
            return "Audio test failed: Cannot create audio context";
        }
    }
    
    if (audioContext.state !== 'running') {
        audioContext.resume().catch(err => {
            console.error("Failed to resume audio context:", err);
            return "Audio test failed: Cannot resume audio context";
        });
    }
    
    try {
        // Create an oscillator for the test tone
        const oscillator = audioContext.createOscillator();
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(440, audioContext.currentTime); // A4 note
        
        // Create a gain node to control volume
        const gainNode = audioContext.createGain();
        gainNode.gain.setValueAtTime(0.1, audioContext.currentTime); // Low volume
        
        // Connect nodes
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        // Schedule playback
        oscillator.start();
        oscillator.stop(audioContext.currentTime + 1); // Play for 1 second
        
        audioPlayerLog("Test tone playing");
        return "Playing test tone...";
    } catch (error) {
        console.error("Error playing test tone:", error);
        return "Audio test failed: " + error.message;
    }
}

/**
 * Signals that the audio stream from the API is complete.
 * Flushes any remaining buffered data.
 */
export function completeAudioStream() {
    if (isStreamComplete) return; // Already marked as complete
    
    audioPlayerLog("Audio stream completion signaled.");
    isStreamComplete = true;

    // If there's remaining data in the processing buffer, queue it now.
    if (processingBuffer.length > 0) {
        audioPlayerLog(`Flushing remaining ${processingBuffer.length} samples from processing buffer.`);
        audioQueue.push(processingBuffer);
        processingBuffer = new Float32Array(0); // Clear the buffer

        // If playback was active but scheduler might have stopped (due to empty queue earlier),
        // ensure it runs at least one more time to schedule the flushed chunk.
        if (isPlaying) {
             // Calling schedulePlayback() ensures the last chunk(s) get scheduled.
             // If a setTimeout was already pending, this might run it slightly sooner.
             audioPlayerLog("Calling schedulePlayback to process final flushed buffer.");
             // Clear any existing timeout before scheduling the final one
             if (scheduleTimeoutId) {
               clearTimeout(scheduleTimeoutId);
               scheduleTimeoutId = null;
             }
             schedulePlayback(); 
        }
    }
    // The schedulePlayback loop will see isStreamComplete=true and set isPlaying=false 
    // once the queue becomes empty after processing the final chunks.
}

/**
 * Reset audio state for new narration
 */
export function resetAudioState() {
    audioPlayerLog("Resetting audio state for new narration");
    
    // Clear seeking support data
    storedChunks = [];
    totalDuration = 0;
    currentPlaybackTime = 0;
    lastUpdateTime = 0;
    
    // Clear other state
    audioQueue = [];
    processingBuffer = new Float32Array(0);
    isStreamComplete = false;
    
    // Stop any active playback
    stopPlayback();
}

/**
 * Stop audio playback and store state for possible resumption
 */
export function stopPlayback() {
    if (!isPlaying) return false; // Already stopped
    
    audioPlayerLog("Stopping audio playback");
    
    // Update current playback time before stopping
    getCurrentTime();
    
    // Immediately clear any pending schedulePlayback timeout
    if (scheduleTimeoutId) {
        clearTimeout(scheduleTimeoutId);
        scheduleTimeoutId = null;
        audioPlayerLog("Cleared pending schedulePlayback timeout.");
    }
    
    // Store current state
    storedAudioQueue = [...audioQueue];
    storedProcessingBuffer = processingBuffer.slice();
    
    // Clear current playback state
    audioQueue = [];
    processingBuffer = new Float32Array(0);
    isPlaying = false;
    
    // Stop and remove any currently scheduled/playing buffer sources
    activeSources.forEach(src => {
        try { src.stop(0); } catch (e) { /* ignore */ }
    });
    activeSources = [];
    
    // Suspend audio context to save resources (optional)
    if (audioContext && audioContext.state === 'running') {
        audioContext.suspend().then(() => {
            audioPlayerLog("AudioContext suspended");
        }).catch(err => {
            console.error("Failed to suspend AudioContext:", err);
        });
    }
    
    return true;
}

/**
 * Resume audio playback from stored state
 */
export function resumePlayback() {
    if (isPlaying) return false; // Already playing
    
    // Check if we have stored data to resume
    if (!storedAudioQueue || !storedProcessingBuffer) {
        audioPlayerLog("No stored audio data to resume");
        return false;
    }
    
    audioPlayerLog("Resuming audio playback");
    
    // Resume AudioContext if suspended
    if (audioContext.state !== 'running') {
        audioContext.resume().then(() => {
            audioPlayerLog("AudioContext resumed for playback resume");
            restorePlaybackState();
        }).catch(err => {
            console.error("Failed to resume AudioContext:", err);
            return false;
        });
    } else {
        restorePlaybackState();
    }
    
    return true;
}

/**
 * Helper function to restore playback state
 * @private
 */
function restorePlaybackState() {
    // Restore stored state
    audioQueue = [...storedAudioQueue];
    processingBuffer = storedProcessingBuffer.slice();
    
    // Clear stored state
    storedAudioQueue = null;
    storedProcessingBuffer = null;
    
    // Only restart playback if we have data
    if (audioQueue.length > 0 || processingBuffer.length > 0) { // Check processing buffer too
        isPlaying = true;
        // Initialize scheduledTime with a small buffer
        scheduledTime = audioContext.currentTime + INITIAL_BUFFER_TIME;
        audioPlayerLog("Resuming playback scheduling. Queue size:", audioQueue.length, "Processing buffer size:", processingBuffer.length);
        // Clear any potentially lingering timeout before starting anew
        if (scheduleTimeoutId) {
            clearTimeout(scheduleTimeoutId);
            scheduleTimeoutId = null;
        }
        schedulePlayback();
    } else {
        audioPlayerLog("Restored state but no audio data found to play.");
        isPlaying = false;
    }
}

/**
 * Check if audio is currently playing
 * @returns {boolean} True if audio is playing
 */
export function isAudioPlaying() {
    // Consider playback active if the scheduler is running OR if any already
    // scheduled AudioBufferSourceNodes are still playing out.
    return isPlaying || activeSources.length > 0;
}

// Discriminated-union style audio state
export const idle           = { tag: 'idle' };                       // no ES, no playback
export const connecting     = (topic, mode, es) => ({ tag: 'connecting', topic, mode, es });
export const playing        = (topic, mode, es) => ({ tag: 'playing', topic, mode, es });
export const paused         = (topic, mode, es, buffered = []) =>
                              ({ tag: 'paused', topic, mode, es, buffered });

export function transition(state, event) {
  switch (state.tag) {
    case 'idle':
      if (event.type === 'START')       return [ connecting(event.topic, event.mode, event.es), ['OPEN_SSE'] ];
      return [ state, [] ];

    case 'connecting':
      if (event.type === 'CONNECTED')   return [ state, ['SET_STATUS_STREAMING'] ];
      if (event.type === 'CHUNK')       return [ playing(state.topic, state.mode, state.es), ['PLAY', event.chunk] ];
      if (event.type === 'STOP')        return [ idle,  ['CLOSE_SSE', 'AUDIO_STOP'] ];
      return [ state, [] ];

    case 'playing':
      if (event.type === 'CHUNK')       return [ state, ['PLAY', event.chunk] ];
      if (event.type === 'PAUSE')       return [ paused(state.topic, state.mode, state.es), ['AUDIO_STOP'] ];
      if (event.type === 'DONE')        return [ state, ['STREAM_COMPLETE'] ];
      if (event.type === 'STOP')        return [ idle,  ['CLOSE_SSE', 'AUDIO_STOP'] ];
      return [ state, [] ];

    case 'paused':
      if (event.type === 'CHUNK')
        return [ paused(state.topic, state.mode, state.es, [...state.buffered, event.chunk]), [] ];
      if (event.type === 'RESUME')
        return [ playing(state.topic, state.mode, state.es), ['AUDIO_RESUME', ...state.buffered.map(c => ['PLAY', c])] ];
      if (event.type === 'STOP')
        return [ idle, ['CLOSE_SSE'] ];
      return [ state, [] ];

    default: return [ state, [] ];
  }
}

/**
 * Register a callback to be invoked when playback truly finishes.
 * @param {() => void} callback
 */
export function setOnPlaybackComplete(callback) {
    onPlaybackComplete = callback;
}

/**
 * Set callback for progress updates
 * @param {(currentTime: number, duration: number) => void} callback
 */
export function setOnProgressUpdate(callback) {
    onProgressUpdate = callback;
}

/**
 * Get current playback time in seconds
 * @returns {number} Current time in seconds
 */
export function getCurrentTime() {
    if (!isPlaying) return currentPlaybackTime;
    
    // Update current time based on how much time has passed since last update
    const now = audioContext ? audioContext.currentTime : Date.now() / 1000;
    const elapsed = now - lastUpdateTime;
    currentPlaybackTime = Math.min(currentPlaybackTime + elapsed, totalDuration);
    lastUpdateTime = now;
    
    return currentPlaybackTime;
}

/**
 * Get total duration in seconds
 * @returns {number} Total duration in seconds
 */
export function getDuration() {
    return totalDuration;
}

/**
 * Seek to a specific time in seconds
 * @param {number} timeInSeconds - Target time to seek to
 */
export function seekTo(timeInSeconds) {
    if (timeInSeconds < 0 || timeInSeconds > totalDuration) {
        console.warn(`Invalid seek time: ${timeInSeconds}. Duration is ${totalDuration}`);
        return;
    }
    
    audioPlayerLog(`Seeking to ${timeInSeconds}s (duration: ${totalDuration}s)`);
    
    // Stop current playback
    const wasPlaying = isPlaying;
    stopPlayback();
    
    // Clear existing queue and processing buffer
    audioQueue = [];
    processingBuffer = new Float32Array(0);
    
    // Calculate which chunks we need to reconstruct from the seek point
    const chunkDuration = BUFFER_SIZE / SAMPLE_RATE; // Duration of each chunk
    const targetChunkIndex = Math.floor(timeInSeconds / chunkDuration);
    const offsetInChunk = timeInSeconds % chunkDuration;
    
    // Reconstruct audio queue from the target point
    for (let i = targetChunkIndex; i < storedChunks.length; i++) {
        const chunk = storedChunks[i];
        if (i === targetChunkIndex && offsetInChunk > 0) {
            // For the first chunk, we need to trim the beginning
            const offsetSamples = Math.floor(offsetInChunk * SAMPLE_RATE);
            if (offsetSamples < chunk.length) {
                audioQueue.push(chunk.slice(offsetSamples));
            }
        } else {
            audioQueue.push(chunk);
        }
    }
    
    // Update current playback time
    currentPlaybackTime = timeInSeconds;
    lastUpdateTime = audioContext ? audioContext.currentTime : Date.now() / 1000;
    
    // Resume playback if it was playing before
    if (wasPlaying && audioQueue.length > 0) {
        resumePlaybackFromSeek();
    }
}

/**
 * Resume playback after seeking
 * @private
 */
function resumePlaybackFromSeek() {
    if (audioContext.state !== 'running') {
        audioContext.resume().then(() => {
            audioPlayerLog("AudioContext resumed after seek");
            startPlaybackAfterSeek();
        }).catch(err => {
            console.error("Failed to resume AudioContext after seek:", err);
        });
    } else {
        startPlaybackAfterSeek();
    }
}

/**
 * Start playback after seeking
 * @private
 */
function startPlaybackAfterSeek() {
    isPlaying = true;
    scheduledTime = audioContext.currentTime + INITIAL_BUFFER_TIME;
    lastUpdateTime = audioContext.currentTime;
    
    if (scheduleTimeoutId) {
        clearTimeout(scheduleTimeoutId);
        scheduleTimeoutId = null;
    }
    
    schedulePlayback();
} 